import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  CheckCircle2, Circle, Clock, FileText, Download, Eye, Package, ShieldCheck,
  Truck, Calendar, MapPin, CreditCard, UserRound, Phone, Mail, PlayCircle,
  AlertTriangle, ShoppingBag, Wrench, HelpCircle, ImageIcon, X, Info, Star, ExternalLink,
} from "lucide-react";

const money = (v) => (v == null ? "—" : `£${Number(v).toLocaleString(undefined, { minimumFractionDigits: 2 })}`);
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }) : "—");

const GREEN_STATUSES = ["quote approved", "deposit paid", "invoice paid", "paid", "warranty active", "completed", "resolved", "active"];
export function StatusPill({ children }) {
  const s = String(children || "").toLowerCase();
  const green = GREEN_STATUSES.some((g) => s.includes(g));
  const amber = ["awaiting", "pending", "action required", "received"].some((g) => s.includes(g));
  const cls = green
    ? "bg-emerald-50 text-emerald-700 border-emerald-200"
    : amber
    ? "bg-amber-50 text-amber-700 border-amber-200"
    : "bg-slate-100 text-slate-600 border-slate-200";
  return <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold border ${cls}`}>{green && <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />}{children}</span>;
}

export function Card({ children, className = "", ...rest }) {
  return <div className={`bg-white border border-slate-200 rounded-2xl shadow-sm ${className}`} {...rest}>{children}</div>;
}

export function EmptyState({ icon: Icon = Info, title, hint }) {
  return (
    <div className="text-center py-14 bg-white rounded-2xl border border-dashed border-slate-200">
      <Icon className="w-8 h-8 text-slate-300 mx-auto mb-2" />
      <p className="text-sm font-medium text-slate-500">{title}</p>
      {hint && <p className="text-xs text-slate-400 mt-1">{hint}</p>}
    </div>
  );
}

function ProductImg({ src, alt, className = "" }) {
  return (
    <div className={`bg-slate-100 overflow-hidden ${className}`}>
      {src ? <img src={src} alt={alt} loading="eager" decoding="async" className="w-full h-full object-cover" /> : null}
    </div>
  );
}

/* ---------------------------- Overview ---------------------------- */
export function Overview({ data, setTab, onContact }) {
  const o = data.order;
  const quick = [
    { label: "View My Order", icon: Package, onClick: () => setTab("purchases") },
    { label: "Download Invoice", icon: Download, onClick: () => setTab("documents") },
    { label: "View Installation", icon: ImageIcon, onClick: () => setTab("installation") },
    { label: "Read Care Guide", icon: FileText, onClick: () => setTab("care") },
    { label: "Contact Support", icon: HelpCircle, onClick: onContact },
  ];
  return (
    <div className="space-y-6" data-testid="portal-overview">
      <Card className="overflow-hidden">
        <div className="grid md:grid-cols-[280px_1fr]">
          <ProductImg src={o.product_image} alt={o.product_name} className="h-56 md:h-full min-h-[220px]" />
          <div className="p-5 sm:p-6">
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-indigo-600">Your order</p>
                <h2 className="text-xl font-bold text-slate-900 mt-0.5">{o.product_name}</h2>
              </div>
              <StatusPill>{o.status}</StatusPill>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-6 gap-y-4 mt-5 text-sm">
              <Field icon={FileText} label="Order reference" value={o.reference} />
              <Field icon={Calendar} label="Order date" value={fmtDate(o.order_date)} />
              <Field icon={Truck} label="Installation date" value={fmtDate(o.installation_date)} />
              <Field icon={CreditCard} label="Payment status" value={<StatusPill>{o.payment_status}</StatusPill>} />
              <Field icon={UserRound} label="Your adviser" value={data.customer.adviser} />
              <Field icon={MapPin} label="Installation address" value={o.installation_address} span />
            </div>
            <Button onClick={onContact} className="mt-5 bg-indigo-600 hover:bg-indigo-700 text-white gap-1.5" data-testid="portal-contact-support-btn">
              <HelpCircle className="w-4 h-4" /> Contact Support
            </Button>
          </div>
        </div>
      </Card>

      <Card className="p-5 sm:p-6">
        <h3 className="text-sm font-bold text-slate-900 mb-5">Order progress</h3>
        <ProgressTracker stages={o.stages} current={o.current_stage} />
      </Card>

      {data.trustpilot && (
        <Card className="p-5 sm:p-6" data-testid="portal-trustpilot">
          <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
            <div className="flex items-center gap-2">
              <span className="inline-flex">
                {Array.from({ length: 5 }).map((_, i) => (
                  <Star key={i} className={`w-4 h-4 ${i < (data.trustpilot.review?.stars || 5) ? "text-emerald-500 fill-emerald-500" : "text-slate-200"}`} />
                ))}
              </span>
              <span className="text-sm text-slate-600"><b className="text-slate-900">{data.trustpilot.score}</b>/5 · {(data.trustpilot.total || 0).toLocaleString()} reviews on Trustpilot</span>
            </div>
            <a href={data.trustpilot.url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs font-medium text-indigo-600 hover:underline" data-testid="portal-trustpilot-link">
              Read more<ExternalLink className="w-3.5 h-3.5" />
            </a>
          </div>
          {data.trustpilot.review?.title && <div className="text-sm font-bold text-slate-900">{data.trustpilot.review.title}</div>}
          <p className="text-sm text-slate-600 mt-1 leading-relaxed">“{data.trustpilot.review?.text}”</p>
          <div className="text-[11px] text-slate-400 mt-2">— {data.trustpilot.review?.author}{data.trustpilot.review?.date ? ` · ${data.trustpilot.review.date}` : ""}</div>
        </Card>
      )}

      <div>
        <h3 className="text-sm font-bold text-slate-900 mb-3">Quick actions</h3>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
          {quick.map((q) => (
            <button key={q.label} onClick={q.onClick} data-testid={`portal-quick-${q.label.replace(/\s+/g, "-").toLowerCase()}`}
              className="group bg-white border border-slate-200 rounded-xl p-4 text-left hover:border-indigo-300 hover:shadow-sm transition-all">
              <q.icon className="w-5 h-5 text-indigo-500 mb-2 group-hover:scale-110 transition-transform" />
              <span className="text-sm font-semibold text-slate-700">{q.label}</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

function Field({ icon: Icon, label, value, span }) {
  return (
    <div className={span ? "col-span-2 sm:col-span-3" : ""}>
      <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-slate-400"><Icon className="w-3.5 h-3.5" /> {label}</div>
      <div className="mt-0.5 text-slate-800 font-medium">{value}</div>
    </div>
  );
}

export function ProgressTracker({ stages, current }) {
  return (
    <ol className="relative flex flex-col sm:flex-row sm:items-start gap-4 sm:gap-0" data-testid="portal-progress">
      {stages.map((s, i) => {
        const done = s.key < current;
        const active = s.key === current;
        return (
          <li key={s.key} className="relative flex sm:flex-col sm:items-center sm:text-center flex-1 gap-3 sm:gap-2">
            {i < stages.length - 1 && (
              <span className={`hidden sm:block absolute top-4 left-1/2 w-full h-0.5 ${done ? "bg-emerald-400" : "bg-slate-200"}`} />
            )}
            <span className={`relative z-10 shrink-0 w-8 h-8 rounded-full flex items-center justify-center border-2 ${
              done ? "bg-emerald-500 border-emerald-500 text-white"
              : active ? "bg-white border-indigo-500 text-indigo-600 ring-4 ring-indigo-100"
              : "bg-white border-slate-300 text-slate-300"}`}>
              {done ? <CheckCircle2 className="w-4 h-4" /> : active ? <Clock className="w-4 h-4" /> : <Circle className="w-3 h-3" />}
            </span>
            <div className="sm:mt-1 sm:px-1">
              <div className={`text-xs font-semibold ${active ? "text-indigo-700" : done ? "text-slate-700" : "text-slate-400"}`}>{s.label}</div>
              {s.date && <div className="text-[11px] text-slate-400">{fmtDate(s.date)}</div>}
              {active && <div className="text-[10px] font-bold uppercase tracking-wide text-indigo-500 mt-0.5">Current</div>}
            </div>
          </li>
        );
      })}
    </ol>
  );
}

/* -------------------------- My Purchases -------------------------- */
export function MyPurchases({ data, notify }) {
  const main = data.purchases.find((p) => p.is_main);
  const rest = data.purchases.filter((p) => !p.is_main);
  if (!data.purchases.length) return <EmptyState icon={Package} title="No purchases to show yet" />;
  return (
    <div className="space-y-6" data-testid="portal-purchases">
      {main && (
        <Card className="overflow-hidden">
          <div className="grid md:grid-cols-[260px_1fr]">
            <ProductImg src={main.image} alt={main.name} className="h-52 md:h-full min-h-[200px]" />
            <div className="p-5 sm:p-6">
              <div className="flex items-center gap-2 mb-1"><span className="text-xs font-semibold uppercase tracking-wide text-indigo-600">Your hot tub</span><StatusPill>Warranty {main.warranty_status}</StatusPill></div>
              <h2 className="text-lg font-bold text-slate-900">{main.name}</h2>
              <div className="grid grid-cols-2 gap-x-6 gap-y-2.5 mt-4 text-sm">
                {Object.entries(main.spec).map(([k, v]) => (
                  <div key={k}><div className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">{k}</div><div className="text-slate-800 font-medium">{v}</div></div>
                ))}
              </div>
              <div className="flex gap-2 mt-5">
                <Button variant="outline" size="sm" className="gap-1.5" onClick={() => notify("Opening product details (demonstration).")}><Eye className="w-4 h-4" /> View Details</Button>
                <Button variant="outline" size="sm" className="gap-1.5" onClick={() => notify("Downloading owner's manual (demonstration).")}><Download className="w-4 h-4" /> Download Manual</Button>
              </div>
            </div>
          </div>
        </Card>
      )}
      <div>
        <h3 className="text-sm font-bold text-slate-900 mb-3">Accessories & consumables</h3>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {rest.map((p) => (
            <Card key={p.id} className="overflow-hidden flex flex-col" data-testid={`portal-purchase-${p.id}`}>
              <ProductImg src={p.image} alt={p.name} className="h-36" />
              <div className="p-4 flex flex-col flex-1">
                <div className="flex items-start justify-between gap-2">
                  <h4 className="text-sm font-bold text-slate-900">{p.name}</h4>
                  <StatusPill>{p.warranty_status === "N/A" ? "Consumable" : p.warranty_status}</StatusPill>
                </div>
                <p className="text-xs text-slate-500 mt-0.5">Model {p.model} · Qty {p.quantity}</p>
                <div className="text-xs text-slate-500 mt-2 space-y-0.5">
                  <div>Ordered {fmtDate(p.order_date)}</div>
                  {p.installation_date && <div>Installed {fmtDate(p.installation_date)}</div>}
                  {p.serial_number && <div>Serial {p.serial_number}</div>}
                </div>
                <div className="flex gap-2 mt-auto pt-3">
                  <Button variant="outline" size="sm" className="h-8 text-xs gap-1" onClick={() => notify("Opening details (demonstration).")}><Eye className="w-3.5 h-3.5" /> Details</Button>
                  <Button variant="outline" size="sm" className="h-8 text-xs gap-1" onClick={() => notify("Downloading manual (demonstration).")}><Download className="w-3.5 h-3.5" /> Manual</Button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      </div>
    </div>
  );
}

/* --------------------------- Documents --------------------------- */
export function Documents({ data, notify }) {
  if (!data.documents.length) return <EmptyState icon={FileText} title="No documents available yet" />;
  return (
    <Card className="overflow-hidden" data-testid="portal-documents">
      <div className="overflow-x-auto">
        <table className="w-full text-sm min-w-[680px]">
          <thead><tr className="bg-slate-50 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-500">
            <th className="px-4 py-3">Document</th><th className="px-4 py-3">Type</th><th className="px-4 py-3">Reference</th><th className="px-4 py-3">Date</th><th className="px-4 py-3">Status</th><th className="px-4 py-3 text-right">Total</th><th className="px-4 py-3 text-right">Actions</th>
          </tr></thead>
          <tbody className="divide-y divide-slate-100">
            {data.documents.map((d) => (
              <tr key={d.id} className="hover:bg-slate-50" data-testid={`portal-doc-${d.id}`}>
                <td className="px-4 py-3 font-medium text-slate-800">{d.title}</td>
                <td className="px-4 py-3 text-slate-500">{d.type}</td>
                <td className="px-4 py-3 text-slate-500 font-mono text-xs">{d.reference}</td>
                <td className="px-4 py-3 text-slate-500">{fmtDate(d.date)}</td>
                <td className="px-4 py-3">{d.status ? <StatusPill>{d.status}</StatusPill> : <span className="text-slate-300">—</span>}</td>
                <td className="px-4 py-3 text-right font-semibold text-slate-800">{d.total != null ? money(d.total) : "—"}</td>
                <td className="px-4 py-3">
                  <div className="flex justify-end gap-1.5">
                    <Button variant="outline" size="sm" className="h-8 text-xs gap-1" onClick={() => notify(`Opening ${d.title} (demonstration).`)}><Eye className="w-3.5 h-3.5" /> View</Button>
                    <Button variant="outline" size="sm" className="h-8 text-xs gap-1" onClick={() => notify(`Downloading ${d.title} (demonstration).`)}><Download className="w-3.5 h-3.5" /> PDF</Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

/* -------------------------- Installation -------------------------- */
export function Installation({ data }) {
  const [active, setActive] = useState(null);
  const g = data.installation.gallery;
  return (
    <div className="grid lg:grid-cols-[1fr_280px] gap-6" data-testid="portal-installation">
      <div>
        <h3 className="text-sm font-bold text-slate-900 mb-3">Installation gallery</h3>
        {g.length === 0 ? <EmptyState icon={ImageIcon} title="No photos shared yet" /> : (
          <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-4">
            {g.map((item) => (
              <button key={item.id} onClick={() => setActive(item)} className="group text-left bg-white border border-slate-200 rounded-xl overflow-hidden hover:shadow-md transition-shadow" data-testid={`portal-gallery-${item.id}`}>
                <div className="relative h-40 bg-slate-100">
                  <img src={item.thumbnail} alt={item.title} loading="lazy" className="w-full h-full object-cover" />
                  {item.type === "video" && <span className="absolute inset-0 flex items-center justify-center bg-black/30"><PlayCircle className="w-10 h-10 text-white" /></span>}
                  <span className="absolute top-2 left-2 text-[10px] font-bold uppercase tracking-wide bg-white/90 text-slate-700 px-2 py-0.5 rounded-full">{item.category}</span>
                </div>
                <div className="p-3">
                  <div className="text-sm font-semibold text-slate-800">{item.title}</div>
                  <div className="text-xs text-slate-400">{fmtDate(item.date)}</div>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
      <div>
        <h3 className="text-sm font-bold text-slate-900 mb-3">Timeline</h3>
        <Card className="p-4">
          <ol className="space-y-4">
            {data.installation.timeline.map((t, i) => (
              <li key={i} className="flex gap-3">
                <span className={`mt-0.5 shrink-0 w-6 h-6 rounded-full flex items-center justify-center ${t.done ? "bg-emerald-500 text-white" : "bg-slate-100 text-slate-400"}`}>
                  {t.done ? <CheckCircle2 className="w-4 h-4" /> : <Circle className="w-3 h-3" />}
                </span>
                <div><div className={`text-sm font-medium ${t.done ? "text-slate-800" : "text-slate-400"}`}>{t.label}</div><div className="text-xs text-slate-400">{t.date ? fmtDate(t.date) : "Pending"}</div></div>
              </li>
            ))}
          </ol>
        </Card>
      </div>

      {active && (
        <div className="fixed inset-0 z-50 bg-slate-900/80 flex items-center justify-center p-4" onClick={() => setActive(null)} data-testid="portal-lightbox">
          <div className="max-w-3xl w-full bg-white rounded-2xl overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="relative bg-slate-100">
              <img src={active.thumbnail} alt={active.title} className="w-full max-h-[70vh] object-contain" />
              {active.type === "video" && <span className="absolute inset-0 flex items-center justify-center"><PlayCircle className="w-16 h-16 text-white/90" /></span>}
              <button onClick={() => setActive(null)} className="absolute top-3 right-3 w-8 h-8 rounded-full bg-white/90 flex items-center justify-center hover:bg-white" data-testid="portal-lightbox-close"><X className="w-4 h-4" /></button>
            </div>
            <div className="p-4 flex items-start justify-between gap-3">
              <div><div className="text-sm font-bold text-slate-900">{active.title}</div><div className="text-xs text-slate-500 mt-0.5">{active.category} · {fmtDate(active.date)}</div><p className="text-sm text-slate-600 mt-2">{active.description}</p></div>
              {active.downloadable && <Button variant="outline" size="sm" className="gap-1.5 shrink-0"><Download className="w-4 h-4" /> Download</Button>}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ---------------------------- My Forms ---------------------------- */
export function MyForms({ data, notify }) {
  if (!data.forms.length) return <EmptyState icon={FileText} title="No forms to show" />;
  return (
    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="portal-forms">
      {data.forms.map((f) => (
        <Card key={f.id} className="p-4 flex flex-col" data-testid={`portal-form-${f.id}`}>
          <div className="flex items-start justify-between gap-2">
            <h4 className="text-sm font-bold text-slate-900">{f.title}</h4>
            {f.status === "Completed" ? <StatusPill>Completed</StatusPill> : f.action_required ? <StatusPill>Action required</StatusPill> : <StatusPill>{f.status}</StatusPill>}
          </div>
          {f.status === "Completed" ? (
            <p className="text-xs text-slate-500 mt-2">Completed {fmtDate(f.completed_at)} by {f.completed_by}</p>
          ) : f.action_required ? (
            <p className="text-xs text-amber-700 mt-2 flex items-start gap-1"><AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" /> {f.action_note}</p>
          ) : (
            <p className="text-xs text-slate-400 mt-2">Not yet available.</p>
          )}
          <div className="flex gap-2 mt-auto pt-3">
            {f.action_required ? (
              <Button size="sm" className="h-8 text-xs gap-1 bg-indigo-600 hover:bg-indigo-700 text-white" onClick={() => notify(`Opening "${f.title}" form (demonstration).`)}>Complete Form</Button>
            ) : (
              <>
                <Button variant="outline" size="sm" className="h-8 text-xs gap-1" onClick={() => notify(`Opening "${f.title}" (read-only demonstration).`)} disabled={f.status !== "Completed"}><Eye className="w-3.5 h-3.5" /> View Form</Button>
                <Button variant="outline" size="sm" className="h-8 text-xs gap-1" onClick={() => notify("Downloading form PDF (demonstration).")} disabled={f.status !== "Completed"}><Download className="w-3.5 h-3.5" /> PDF</Button>
              </>
            )}
          </div>
        </Card>
      ))}
    </div>
  );
}

/* ----------------------- Care & Accessories ----------------------- */
export function CareAccessories({ data, notify }) {
  return (
    <div data-testid="portal-care">
      <div className="mb-4">
        <h3 className="text-base font-bold text-slate-900">Recommended for your {data.product_model}</h3>
        <p className="text-sm text-slate-500">Genuine, compatible parts and care products for your spa.</p>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {data.care_products.map((p) => (
          <Card key={p.id} className="overflow-hidden flex flex-col" data-testid={`portal-care-${p.id}`}>
            <ProductImg src={p.image} alt={p.name} className="h-40" />
            <div className="p-4 flex flex-col flex-1">
              <h4 className="text-sm font-bold text-slate-900">{p.name}</h4>
              <p className="text-xs text-emerald-700 mt-1 flex items-start gap-1"><ShieldCheck className="w-3.5 h-3.5 mt-0.5 shrink-0" /> {p.compatibility}</p>
              {p.replace_note && <p className="text-xs text-slate-500 mt-2"><span className="font-semibold text-slate-600">When should I replace this?</span> {p.replace_note}</p>}
              <div className="flex items-center justify-between mt-3">
                <span className="text-base font-bold text-slate-900">{money(p.price)}</span>
                {p.in_stock ? <StatusPill>In stock</StatusPill> : <span className="text-xs font-semibold text-slate-400">Out of stock</span>}
              </div>
              <div className="flex gap-2 mt-3">
                <Button variant="outline" size="sm" className="h-8 text-xs gap-1 flex-1" asChild>
                  <a href={p.buy_url} target="_blank" rel="noreferrer"><Eye className="w-3.5 h-3.5" /> View Product</a>
                </Button>
                {p.in_stock ? (
                  <Button size="sm" className="h-8 text-xs gap-1 flex-1 bg-indigo-600 hover:bg-indigo-700 text-white" asChild>
                    <a href={p.buy_url} target="_blank" rel="noreferrer"><ShoppingBag className="w-3.5 h-3.5" /> Buy Online</a>
                  </Button>
                ) : (
                  <Button size="sm" className="h-8 text-xs gap-1 flex-1 bg-slate-700 hover:bg-slate-800 text-white" onClick={() => notify(`Request logged for ${p.name} (demonstration).`)}>Request</Button>
                )}
              </div>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}

/* ----------------------------- Support ---------------------------- */
export function Support({ data, requests, onReport }) {
  const actions = [
    { label: "Report a Fault", icon: AlertTriangle, cat: "Fault" },
    { label: "Request a Service", icon: Wrench, cat: "Service" },
    { label: "Ask a Question", icon: HelpCircle, cat: "Question" },
  ];
  return (
    <div className="grid lg:grid-cols-[1fr_300px] gap-6" data-testid="portal-support">
      <div className="space-y-6">
        <div className="grid sm:grid-cols-3 gap-3">
          {actions.map((a) => (
            <button key={a.label} onClick={() => onReport(a.cat)} data-testid={`portal-support-${a.cat.toLowerCase()}`}
              className="bg-white border border-slate-200 rounded-xl p-4 text-left hover:border-indigo-300 hover:shadow-sm transition-all">
              <a.icon className="w-5 h-5 text-indigo-500 mb-2" />
              <div className="text-sm font-semibold text-slate-800">{a.label}</div>
            </button>
          ))}
        </div>

        <div>
          <h3 className="text-sm font-bold text-slate-900 mb-3">Your support requests</h3>
          {requests.length === 0 ? <EmptyState icon={HelpCircle} title="No support requests yet" hint="Report a fault or ask a question above." /> : (
            <div className="space-y-3">
              {requests.map((r) => (
                <Card key={r.id} className="p-4" data-testid={`portal-support-req-${r.id}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div><div className="text-sm font-bold text-slate-900">{r.subject}</div><div className="text-xs text-slate-400 mt-0.5">{r.id} · {r.category} · {fmtDate(r.created_at)} · {r.urgency} urgency</div></div>
                    <StatusPill>{r.status}</StatusPill>
                  </div>
                  {r.last_update && <p className="text-sm text-slate-600 mt-2">{r.last_update}</p>}
                </Card>
              ))}
            </div>
          )}
        </div>
      </div>

      <Card className="p-5 h-fit">
        <h3 className="text-sm font-bold text-slate-900 mb-3">Talk to us</h3>
        <a href={`tel:${data.support.phone}`} className="flex items-center gap-3 py-2.5 text-sm text-slate-700 hover:text-indigo-600"><Phone className="w-4 h-4 text-indigo-500" /> {data.support.phone}</a>
        <a href={`mailto:${data.support.email}`} className="flex items-center gap-3 py-2.5 text-sm text-slate-700 hover:text-indigo-600 border-t border-slate-100"><Mail className="w-4 h-4 text-indigo-500" /> {data.support.email}</a>
        <p className="text-xs text-slate-400 mt-3">Mon–Fri 9am–5pm. Demonstration contact details.</p>
      </Card>
    </div>
  );
}
